import { supabase } from "@/lib/supabase";

export type PushCategory =
  | "signals"
  | "signal_results"
  | "strategy"
  | "learning"
  | "operations"
  | "billing"
  | "entitlement";

export const PUSH_CATEGORIES: Array<{ key: PushCategory; label: string; description: string }> = [
  { key: "signals", label: "New production signals", description: "New signals you are entitled to receive." },
  { key: "signal_results", label: "Signal results", description: "Win, loss, breakeven, and invalidation updates." },
  { key: "strategy", label: "Strategy lifecycle", description: "Promotion, rollback, and retirement events." },
  { key: "learning", label: "Learning recommendations", description: "Shadow evidence and candidate-review alerts." },
  { key: "operations", label: "System operations", description: "Worker, provider, and important system alerts." },
  { key: "billing", label: "Billing & subscription", description: "Payment and subscription status changes." },
  { key: "entitlement", label: "Access changes", description: "Signal-access and entitlement changes." },
];

function base64UrlToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padded = value + "=".repeat((4 - (value.length % 4)) % 4);
  const normalized = padded.replace(/-/g, "+").replace(/_/g, "/");
  const binary = window.atob(normalized);
  const buffer = new ArrayBuffer(binary.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) output[i] = binary.charCodeAt(i);
  return output;
}

function browserPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function pushConfigurationAvailable(): boolean {
  return browserPushSupported() && Boolean(import.meta.env.VITE_VAPID_PUBLIC_KEY);
}

export async function getBrowserPushSubscription(): Promise<PushSubscription | null> {
  if (!browserPushSupported()) return null;
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.getSubscription();
}

export async function enablePushNotifications(userId: string): Promise<PushSubscription> {
  if (!browserPushSupported()) throw new Error("This browser does not support Web Push notifications.");
  const publicKey = import.meta.env.VITE_VAPID_PUBLIC_KEY;
  if (!publicKey) throw new Error("Push notifications are not configured for this deployment yet.");

  const currentPermission = Notification.permission;
  const permission = currentPermission === "default"
    ? await Notification.requestPermission()
    : currentPermission;

  if (permission !== "granted") {
    if (permission === "denied") {
      throw new Error("Browser notifications are blocked for FI Radar. In Chrome, open the site controls (lock/tune icon) → Notifications → Allow, then return to FI Radar and press Enable again.");
    }
    throw new Error("Notification permission was not granted. Please allow notifications when Chrome asks.");
  }

  const registration = await navigator.serviceWorker.ready;
  let existing = await registration.pushManager.getSubscription();

  // A push provider can permanently expire an endpoint (HTTP 410). If the
  // browser still exposes that stale subscription, replace it instead of
  // re-registering the same dead endpoint forever.
  if (existing) {
    const { data: stored } = await supabase
      .from("push_subscriptions")
      .select("is_active")
      .eq("user_id", userId)
      .eq("endpoint", existing.endpoint)
      .maybeSingle();

    if (stored?.is_active === false) {
      await existing.unsubscribe();
      existing = null;
    }
  }

  const subscription = existing ?? await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlToUint8Array(publicKey),
  });

  const json = subscription.toJSON();
  const endpoint = json.endpoint;
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!endpoint || !p256dh || !auth) throw new Error("The browser returned an incomplete push subscription.");

  const { error } = await supabase.from("push_subscriptions").upsert({
    user_id: userId,
    endpoint,
    expiration_time: json.expirationTime ? new Date(json.expirationTime).toISOString() : null,
    p256dh,
    auth,
    user_agent: navigator.userAgent.slice(0, 500),
    is_active: true,
    last_error: null,
  }, { onConflict: "endpoint" });
  if (error) throw new Error(`Could not save push subscription: ${error.message}`);

  return subscription;
}

export async function disablePushNotifications(userId: string): Promise<void> {
  const subscription = await getBrowserPushSubscription();
  if (!subscription) return;
  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();
  const { error } = await supabase.from("push_subscriptions").delete().eq("user_id", userId).eq("endpoint", endpoint);
  if (error) throw new Error(`Could not remove push subscription: ${error.message}`);
}

export async function loadPushPreferences(userId: string): Promise<Record<PushCategory, boolean>> {
  const defaults = Object.fromEntries(PUSH_CATEGORIES.map(({ key }) => [key, true])) as Record<PushCategory, boolean>;
  const { data, error } = await supabase.from("push_notification_preferences").select("category,enabled").eq("user_id", userId);
  if (error) throw new Error(`Could not load push preferences: ${error.message}`);
  for (const row of data ?? []) {
    if (row.category in defaults) defaults[row.category as PushCategory] = Boolean(row.enabled);
  }
  return defaults;
}

export async function setPushPreference(userId: string, category: PushCategory, enabled: boolean): Promise<void> {
  const { error } = await supabase.from("push_notification_preferences").upsert({
    user_id: userId,
    category,
    enabled,
  }, { onConflict: "user_id,category" });
  if (error) throw new Error(`Could not save push preference: ${error.message}`);
}
